/**
 * LOD-менеджер интерактивной гиперболической карты.
 *
 * Отвечает за подбор уровня детализации (LOD) под текущий зум, отсечение
 * (culling) треугольников по видимой области и кэширование построенных тайлов,
 * освобождая детальные уровни при отдалении. Модуль **не** содержит DOM/WebGL —
 * он производит только геометрию (треугольники в координатах поверхности),
 * которую затем потребляет {@link module:webgl-map-renderer.WebGLMapRenderer}.
 * Благодаря этому вся логика LOD покрывается юнит-тестами.
 *
 * Схема работы:
 * 1. **Выбор уровня** ({@link selectLodLevel}): при приближении (рост масштаба
 *    отображения) выбирается более глубокий уровень триангуляции; уровень
 *    ограничивается сверху масштабом карты (`maxLevelForScale`) — Требования 3.2, 3.3.
 * 2. **Culling** ({@link triangleInBox}): строятся/запрашиваются только
 *    треугольники, пересекающие видимую область; используется иерархический
 *    спуск по дереву триангуляции с отсечением невидимых поддеревьев и обход
 *    соседних копий фундаментальной области через элементы фуксовой группы —
 *    Требование 3.6.
 * 3. **Кэш тайлов**: построенные треугольники кэшируются по адресу
 *    `(rootIndex, childPath)`, чтобы переиспользоваться между кадрами.
 * 4. **Освобождение детализации**: при отдалении (уменьшении выбранного уровня)
 *    записи кэша с более глубоким уровнем немедленно вытесняются, а размер
 *    кэша ограничивается по стратегии LRU — Требование 3.4.
 * 5. **Единый перечень видимых копий** ({@link LodManager.getVisibleCopies}):
 *    во время `update` накапливаются копии `γ`, давшие хотя бы один треугольник,
 *    прошедший `triangleInBox` в координатах `V∘γ`; перечень кэшируется до
 *    следующего кадра, ограничен потолком {@link MAX_VISIBLE_COPIES} и служит
 *    общим источником копий для меша, рисунков и меток — Требования 4.1, 4.2,
 *    4.3, 4.5.
 *
 * Требования: 3.2, 3.3, 3.4, 3.6, 4.1, 4.2, 4.3, 4.5
 */

import type {
  FundamentalPolygon,
  Geometry,
  GeometryType,
  Isometry,
  Mobius,
  SidePairingResult,
  SurfacePairing,
  Triangle,
  TrianglePath,
} from '@langton/shared';
import {
  asIsometry,
  createGeometry,
  enumerateNeighbors,
  enumerateSurfaceNeighbors,
  enumerateVertexNeighbors,
  isometryIdentity,
  triangleFromPath,
  maxLevelForScale,
} from '@langton/shared';

/**
 * Режим обхода копий фундаментальной области при перечислении соседних плиток.
 *
 * - `'depth'` — BFS по образующим фуксовой группы, ограниченный длиной слова
 *   {@link LodManagerOptions.neighborDepth} (историческое поведение).
 * - `'vertex'` — «вершинное кольцо»: все копии, примыкающие к области в вершине
 *   ({@link enumerateVertexNeighbors}), без ограничения по длине слова. Собирает
 *   полный цикл копий вокруг каждой вершины многоугольника, включая копии с
 *   противоположной стороны области, которые при малой глубине BFS пропускаются.
 */
export type CopyTraversal = 'depth' | 'vertex';

/** Половинные размеры видимой области в координатах диска (после применения вида). */
export interface VisibleExtents {
  /** Половина ширины видимой области по оси X (в координатах диска). */
  hx: number;
  /** Половина высоты видимой области по оси Y (в координатах диска). */
  hy: number;
}

/** Информация о вьюпорте, необходимая для отсечения по видимой области. */
export interface ViewportInfo {
  /** Ширина холста в пикселях (`> 0`). */
  width: number;
  /** Высота холста в пикселях (`> 0`). */
  height: number;
}

/** Опции конфигурации {@link LodManager}. */
export interface LodManagerOptions {
  /**
   * Масштаб карты (число уровней LOD). Ограничивает максимальный выбираемый
   * уровень через `maxLevelForScale(mapScale)` (Требования 3.2, 4.5).
   * По умолчанию `1`.
   */
  mapScale?: number;
  /**
   * Масштаб отображения диска при максимальном отдалении (видна ровно одна
   * фундаментальная область). Используется как опорная точка выбора уровня.
   * По умолчанию `1` (весь диск вписан в видимую область).
   */
  minScale?: number;
  /**
   * Сколько уровней детализации добавляется на каждое удвоение масштаба
   * отображения относительно {@link minScale}. По умолчанию `1`.
   */
  levelsPerZoomDoubling?: number;
  /**
   * Глубина перечисления соседних плиток (BFS по образующим фуксовой группы),
   * если во второй параметр конструктора передан {@link SidePairingResult}.
   * По умолчанию `8` — не меньше числа сторон цикла вокруг вершины восьмиугольника
   * рода 2, чтобы вершинные соседи попадали в кандидаты (Требования 4.2, 4.3).
   */
  neighborDepth?: number;
  /**
   * Ограничение на число соседних плиток при перечислении. По умолчанию `256`
   * ({@link MAX_VISIBLE_COPIES}) — потолок копий на кадр (Требование 4.5).
   */
  maxNeighbors?: number;
  /**
   * Реализация геометрии карты (Требования 6, 8): определяет действие изометрий
   * при преобразовании треугольников/копий, метрику середин при подразбиении и
   * проекцию для отсечения. По умолчанию гиперболическая (обратная совместимость).
   * Имеет приоритет над {@link LodManagerOptions.geometryType}.
   */
  geometry?: Geometry;
  /** Тип геометрии карты; используется, если не задан {@link LodManagerOptions.geometry}. */
  geometryType?: GeometryType;
  /**
   * Относительный запас (доля половинного размера видимой области), на который
   * расширяется область отсечения. Помогает не отсекать треугольники у самой
   * кромки экрана. По умолчанию `0.15`.
   */
  cullMargin?: number;
  /**
   * Максимальное число записей в кэше тайлов. При превышении вытесняются
   * наименее недавно использованные записи (LRU). По умолчанию `8192`.
   */
  maxCacheSize?: number;
  /**
   * Режим обхода копий при перечислении соседних плиток из склейки
   * ({@link SidePairingResult}/{@link SurfacePairing}). По умолчанию `'depth'`
   * (BFS по длине слова с глубиной {@link LodManagerOptions.neighborDepth}).
   * `'vertex'` включает вершинный обход {@link enumerateVertexNeighbors} —
   * полный цикл копий вокруг каждой вершины области без ограничения по глубине.
   * Игнорируется, если соседи переданы готовым массивом изометрий.
   */
  copyTraversal?: CopyTraversal;
}

/** Запись кэша тайла: построенный треугольник в координатах поверхности. */
interface CachedTile {
  /** Вершины треугольника в координатах фундаментальной области (без соседа/вида). */
  triangle: Triangle;
  /** Уровень детализации (длина `childPath`). */
  level: number;
  /** Номер кадра последнего использования (для LRU/вытеснения). */
  lastUsedFrame: number;
}

/**
 * Жёсткий потолок числа копий фундаментальной области на кадр (Требование 4.5).
 *
 * Единый перечень видимых копий ({@link LodManager.getVisibleCopies}) — общий
 * источник для меша, рисунков и меток — ограничивается сверху этим значением:
 * обход соседей прекращается по достижении {@link MAX_VISIBLE_COPIES} видимых
 * копий, что удерживает стоимость кадра ограниченной независимо от входа.
 */
export const MAX_VISIBLE_COPIES = 256;

/** Значения по умолчанию для опций менеджера. */
const DEFAULTS = {
  mapScale: 1,
  minScale: 1,
  levelsPerZoomDoubling: 1,
  // Глубина BFS по образующим фуксовой группы. Для восьмиугольника рода 2 в
  // каждой вершине сходятся 8 копий, а слова, порождающие вершинных соседей,
  // имеют длину до числа сторон цикла вокруг вершины (8). Поэтому глубина
  // должна быть не меньше 8, чтобы вершинные соседи попадали в кандидаты
  // (Требования 4.2, 4.3). Фактическое отсечение делает `triangleInBox`.
  neighborDepth: 8,
  // Потолок числа кандидатов-соседей на кадр (Требование 4.5).
  maxNeighbors: MAX_VISIBLE_COPIES,
  cullMargin: 0.15,
  maxCacheSize: 8192,
  // Обход копий по умолчанию — по длине слова (историческое поведение).
  copyTraversal: 'depth',
} as const;

/**
 * Выбрать уровень детализации по текущему масштабу отображения.
 *
 * При масштабе, равном {@link LodManagerOptions.minScale}, выбирается уровень `0`
 * (самые крупные треугольники — максимальное отдаление). Каждое удвоение масштаба
 * добавляет `levelsPerZoomDoubling` уровней (Требования 3.2, 3.3). Результат
 * зажимается в `[0, maxLevel]`, где `maxLevel = maxLevelForScale(mapScale)`.
 *
 * @param displayScale текущий масштаб отображения диска (`> 0`)
 * @param options параметры (используются `mapScale`, `minScale`, `levelsPerZoomDoubling`)
 * @returns целый уровень детализации из `[0, maxLevelForScale(mapScale)]`
 */
export function selectLodLevel(
  displayScale: number,
  options: LodManagerOptions = {},
): number {
  const mapScale = options.mapScale ?? DEFAULTS.mapScale;
  const minScale = options.minScale ?? DEFAULTS.minScale;
  const perDoubling = options.levelsPerZoomDoubling ?? DEFAULTS.levelsPerZoomDoubling;
  const maxLevel = maxLevelForScale(mapScale);

  if (!Number.isFinite(displayScale) || displayScale <= 0 || minScale <= 0) {
    return 0;
  }
  const ratio = Math.max(1, displayScale / minScale);
  const rawLevel = Math.log2(ratio) * perDoubling;
  const level = Math.round(rawLevel);
  return Math.min(Math.max(level, 0), maxLevel);
}

/**
 * Вычислить половинные размеры видимой области в координатах диска (после вида).
 *
 * Соответствует проекции вершинного шейдера рендерера: `clip = w · scale` с
 * поправкой на соотношение сторон (деление `clip.x` на `aspect` при `aspect ≥ 1`,
 * умножение `clip.y` на `aspect` иначе). Видимой считается область `|clip| ≤ 1`.
 * Результат расширяется относительным запасом `margin`.
 *
 * @param scale масштаб отображения диска (`> 0`)
 * @param aspect соотношение сторон холста `width / height` (`> 0`)
 * @param margin относительный запас расширения (`≥ 0`)
 */
export function visibleExtents(scale: number, aspect: number, margin = 0): VisibleExtents {
  if (!Number.isFinite(scale) || scale <= 0 || !Number.isFinite(aspect) || aspect <= 0) {
    return { hx: 1, hy: 1 };
  }
  let hx: number;
  let hy: number;
  if (aspect >= 1) {
    hx = aspect / scale;
    hy = 1 / scale;
  } else {
    hx = 1 / scale;
    hy = 1 / (aspect * scale);
  }
  const factor = 1 + Math.max(0, margin);
  return { hx: hx * factor, hy: hy * factor };
}

/**
 * Проверить, пересекается ли треугольник с прямоугольной видимой областью
 * `[−hx, hx] × [−hy, hy]` (проверка перекрытия ограничивающих прямоугольников).
 *
 * Используется консервативное отсечение по AABB: треугольник считается видимым,
 * если его ограничивающий прямоугольник пересекает видимый. Возможны ложные
 * срабатывания (bbox пересекается, а сам треугольник — нет), но видимый
 * треугольник никогда не отсекается ошибочно (Требование 3.6).
 *
 * @param tri треугольник в координатах отображения (после применения вида)
 * @param hx половина ширины видимой области
 * @param hy половина высоты видимой области
 */
export function triangleInBox(tri: Triangle, hx: number, hy: number): boolean {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const v of tri) {
    if (v.re < minX) minX = v.re;
    if (v.re > maxX) maxX = v.re;
    if (v.im < minY) minY = v.im;
    if (v.im > maxY) maxY = v.im;
  }
  return minX <= hx && maxX >= -hx && minY <= hy && maxY >= -hy;
}

/** Применить обобщённую изометрию `iso` ко всем вершинам треугольника (в метрике геометрии). */
function transformTriangle(geometry: Geometry, iso: Isometry, tri: Triangle): Triangle {
  return [geometry.apply(iso, tri[0]), geometry.apply(iso, tri[1]), geometry.apply(iso, tri[2])];
}

/**
 * Треугольник в координатах отображения для отсечения: применяет копию `γ`,
 * затем вид `V`, затем проекцию геометрии в клип-координаты
 * (`project(V(γ(v)))`). Для гиперболической/евклидовой геометрии `project` —
 * тождество, поэтому совпадает с прежним `apply(compose(view, gamma), v)`; для
 * сферической выполняется ортографическая проекция, согласованная с шейдером.
 */
function displayTriangle(
  geometry: Geometry,
  view: Isometry,
  gamma: Isometry,
  tri: Triangle,
): Triangle {
  return [
    geometry.project(geometry.apply(view, geometry.apply(gamma, tri[0]))),
    geometry.project(geometry.apply(view, geometry.apply(gamma, tri[1]))),
    geometry.project(geometry.apply(view, geometry.apply(gamma, tri[2]))),
  ];
}

/** Построить строковый ключ адреса тайла `(rootIndex, childPath)`. */
function pathKey(path: TrianglePath): string {
  return `${path.rootIndex}:${path.childPath.join('')}`;
}

/**
 * Менеджер уровней детализации: подбирает уровень, отсекает невидимые
 * треугольники, кэширует тайлы и освобождает детализацию при отдалении.
 *
 * Жизненный цикл:
 * 1. `new LodManager(polygon, neighbors, options)` — задаёт геометрию, соседние
 *    копии и параметры.
 * 2. `update(view, scale, viewport)` — на каждый кадр возвращает набор
 *    треугольников (в координатах поверхности) для отрисовки.
 * 3. `getTriangles()` / `getLevel()` / `getVisibleCopies()` — результат
 *    последнего кадра (треугольники, уровень, видимые копии `γ`).
 * 4. `clearCache()` — сброс кэша.
 */
export class LodManager {
  private readonly polygon: FundamentalPolygon;
  private readonly rootCount: number;
  /** Соседние копии как обобщённые изометрии (матрица + признак обращения). */
  private readonly neighbors: Isometry[];
  /** Геометрия карты (действие изометрий, метрика подразбиения, проекция). */
  private readonly geometry: Geometry;
  private readonly options: Required<Omit<LodManagerOptions, 'geometry' | 'geometryType'>>;

  /** Кэш построенных тайлов по ключу адреса `(rootIndex, childPath)`. */
  private readonly cache = new Map<string, CachedTile>();

  /** Треугольники последнего кадра (в координатах поверхности, с учётом соседа). */
  private triangles: Triangle[] = [];
  /**
   * Видимые копии `γ` последнего кадра: элементы группы, давшие хотя бы один
   * треугольник, прошедший {@link triangleInBox} в координатах `V∘γ`. Единый
   * перечень для меша, рисунков и меток (Требования 4.1, 4.2, 4.3, 4.5).
   * Кэшируется до следующего {@link update}.
   */
  private visibleCopies: Isometry[] = [];
  /**
   * Матрицы видимых копий последнего кадра (проекция {@link visibleCopies}).
   * Кэшируется до следующего {@link update}, чтобы {@link getVisibleCopies}
   * возвращал стабильную ссылку (единый перечень для меша, рисунков и меток).
   */
  private visibleCopiesMatrices: Mobius[] = [];
  /** Выбранный уровень детализации последнего кадра. */
  private level = 0;
  /** Счётчик кадров (монотонно растёт при каждом `update`). */
  private frame = 0;

  /**
   * @param polygon фундаментальный многоугольник (вершины в координатах диска)
   * @param neighbors соседние копии фундаментальной области: массив изометрий
   *   (включая/исключая тождество), либо {@link SidePairingResult} для
   *   автоматического перечисления соседей, либо `null` (только одна копия)
   * @param options параметры выбора уровня, отсечения и кэша
   */
  constructor(
    polygon: FundamentalPolygon,
    neighbors?: Mobius[] | Isometry[] | SidePairingResult | SurfacePairing | null,
    options: LodManagerOptions = {},
  ) {
    this.polygon = polygon;
    this.rootCount = polygon.vertices.length;
    // Геометрия: явная реализация имеет приоритет, иначе по типу (по умолчанию
    // гиперболическая — обратная совместимость). Задаётся до resolveNeighbors,
    // который использует её для перечисления соседей произвольной геометрии.
    this.geometry = options.geometry ?? createGeometry(options.geometryType ?? 'hyperbolic');
    this.options = {
      mapScale: options.mapScale ?? DEFAULTS.mapScale,
      minScale: options.minScale ?? DEFAULTS.minScale,
      levelsPerZoomDoubling: options.levelsPerZoomDoubling ?? DEFAULTS.levelsPerZoomDoubling,
      neighborDepth: options.neighborDepth ?? DEFAULTS.neighborDepth,
      maxNeighbors: options.maxNeighbors ?? DEFAULTS.maxNeighbors,
      cullMargin: options.cullMargin ?? DEFAULTS.cullMargin,
      maxCacheSize: options.maxCacheSize ?? DEFAULTS.maxCacheSize,
      copyTraversal: options.copyTraversal ?? DEFAULTS.copyTraversal,
    };
    this.neighbors = this.resolveNeighbors(neighbors);
  }

  /**
   * Привести переданные соседние копии к массиву изометрий, гарантированно
   * содержащему тождество первым элементом.
   */
  private resolveNeighbors(
    neighbors?: Mobius[] | Isometry[] | SidePairingResult | SurfacePairing | null,
  ): Isometry[] {
    if (!neighbors) {
      return [isometryIdentity()];
    }
    if (Array.isArray(neighbors)) {
      const list = neighbors.map(asIsometry);
      return list.length > 0 ? list : [isometryIdentity()];
    }
    const options = {
      maxDepth: this.options.neighborDepth,
      maxCount: this.options.maxNeighbors,
    };
    const vertex = this.options.copyTraversal === 'vertex';
    // Новый SurfacePairing (изометрии) → перечисление произвольной геометрии и
    // неориентируемых склеек; устаревший SidePairingResult (матрицы) → прежний
    // гиперболический перебор (обратная совместимость), матрицы поднимаются.
    // В режиме `'vertex'` вместо BFS по длине слова выполняется вершинный обход
    // (полный цикл копий вокруг каждой вершины области), общий для обеих склеек.
    if (isSurfacePairing(neighbors)) {
      const list = vertex
        ? enumerateVertexNeighbors(this.polygon, neighbors.all, options, this.geometry)
        : enumerateSurfaceNeighbors(neighbors, options, this.geometry);
      return list.length > 0 ? list : [isometryIdentity()];
    }
    const list = vertex
      ? enumerateVertexNeighbors(
          this.polygon,
          neighbors.all.map(asIsometry),
          options,
          this.geometry,
        )
      : enumerateNeighbors(neighbors, options).map(asIsometry);
    return list.length > 0 ? list : [isometryIdentity()];
  }

  /**
   * Пересчитать набор видимых треугольников для текущего вида и масштаба.
   *
   * Выбирается уровень детализации по масштабу (Требования 3.2, 3.3); для каждой
   * соседней копии выполняется иерархический спуск по дереву триангуляции с
   * отсечением поддеревьев вне видимой области (Требование 3.6). Построенные
   * тайлы кэшируются; при отдалении детальные уровни освобождаются
   * (Требование 3.4). Попутно накапливается единый перечень видимых копий `γ`
   * (давших хотя бы один треугольник в кадре), доступный через
   * {@link getVisibleCopies} и ограниченный потолком {@link MAX_VISIBLE_COPIES}
   * (Требования 4.1, 4.2, 4.3, 4.5).
   *
   * @param view текущий вид `V` (изометрия диска)
   * @param displayScale масштаб отображения диска (`> 0`)
   * @param viewport размеры холста для расчёта видимой области
   * @returns треугольники кадра в координатах поверхности (с учётом соседа)
   */
  update(view: Mobius | Isometry, displayScale: number, viewport: ViewportInfo): Triangle[] {
    this.frame += 1;
    const targetLevel = selectLodLevel(displayScale, this.options);
    this.level = targetLevel;

    // Вид как обобщённая изометрия (матрица + признак обращения). Для матрицы
    // (обратная совместимость) обращение ориентации отсутствует.
    const viewIso = asIsometry(view);

    const aspect = viewport.height > 0 ? viewport.width / viewport.height : 1;
    const { hx, hy } = visibleExtents(displayScale, aspect, this.options.cullMargin);

    const result: Triangle[] = [];
    const visible: Isometry[] = [];
    for (const gamma of this.neighbors) {
      // Жёсткий потолок копий на кадр: обход прекращается по достижении
      // MAX_VISIBLE_COPIES видимых копий (Требование 4.5).
      if (visible.length >= MAX_VISIBLE_COPIES) {
        break;
      }
      const countBefore = result.length;
      for (let rootIndex = 0; rootIndex < this.rootCount; rootIndex++) {
        this.descend({ rootIndex, childPath: [] }, targetLevel, viewIso, gamma, hx, hy, result);
      }
      // Копия видима тогда и только тогда, когда дала хотя бы один треугольник,
      // прошедший `triangleInBox` в координатах отображения (Требования 4.1–4.3).
      if (result.length > countBefore) {
        visible.push(gamma);
      }
    }

    // Освободить детализацию глубже текущего уровня (Требование 3.4) и
    // ограничить размер кэша по LRU.
    this.evictDeeperThan(targetLevel);
    this.enforceCacheLimit();

    this.triangles = result;
    // Кэшировать перечень видимых копий до следующего update (Требование 4.1).
    // Матрицы кэшируются отдельно, чтобы getVisibleCopies() возвращал стабильную
    // ссылку между вызовами в пределах одного кадра.
    this.visibleCopies = visible;
    this.visibleCopiesMatrices = visible.map((c) => c.matrix);
    return result;
  }

  /**
   * Иерархический спуск по дереву триангуляции с отсечением невидимых поддеревьев.
   *
   * Если текущий треугольник не пересекает видимую область — всё его поддерево
   * пропускается (culling, Требование 3.6). На целевом уровне треугольник
   * добавляется в результат в координатах поверхности (после применения соседа
   * `γ`, но без вида — вид применяет рендерер).
   */
  private descend(
    path: TrianglePath,
    targetLevel: number,
    view: Isometry,
    gamma: Isometry,
    hx: number,
    hy: number,
    out: Triangle[],
  ): void {
    const base = this.getBaseTriangle(path);
    // Отсечение выполняется в координатах отображения: project(V(γ(v))).
    const displayed = displayTriangle(this.geometry, view, gamma, base);
    if (!triangleInBox(displayed, hx, hy)) {
      return;
    }
    if (path.childPath.length >= targetLevel) {
      // В результат — координаты поверхности после копии γ (вид применяет
      // рендерер тем же конвейером); для обращающей ориентацию γ действие
      // антиголоморфно (Требование 8).
      out.push(transformTriangle(this.geometry, gamma, base));
      return;
    }
    for (let child = 0; child < 4; child++) {
      this.descend(
        { rootIndex: path.rootIndex, childPath: [...path.childPath, child] },
        targetLevel,
        view,
        gamma,
        hx,
        hy,
        out,
      );
    }
  }

  /**
   * Получить базовый треугольник (в координатах фундаментальной области) по
   * адресу, используя кэш. Отсутствующий тайл строится через `triangleFromPath`
   * и сохраняется в кэше для переиспользования между кадрами.
   */
  private getBaseTriangle(path: TrianglePath): Triangle {
    const key = pathKey(path);
    const cached = this.cache.get(key);
    if (cached) {
      cached.lastUsedFrame = this.frame;
      return cached.triangle;
    }
    // Подразбиение учитывает геометрию (середины сторон в её метрике); для
    // гиперболической совпадает с прежним поведением (Требование 6).
    const triangle = triangleFromPath(this.polygon, path, this.geometry);
    this.cache.set(key, {
      triangle,
      level: path.childPath.length,
      lastUsedFrame: this.frame,
    });
    return triangle;
  }

  /**
   * Вытеснить из кэша записи с уровнем глубже `level` (освобождение детализации
   * при отдалении, Требование 3.4).
   */
  private evictDeeperThan(level: number): void {
    for (const [key, tile] of this.cache) {
      if (tile.level > level) {
        this.cache.delete(key);
      }
    }
  }

  /** Ограничить размер кэша, вытесняя наименее недавно использованные записи. */
  private enforceCacheLimit(): void {
    const limit = this.options.maxCacheSize;
    if (this.cache.size <= limit) {
      return;
    }
    // Сортировка ключей по возрастанию времени последнего использования.
    const entries = [...this.cache.entries()].sort(
      (a, b) => a[1].lastUsedFrame - b[1].lastUsedFrame,
    );
    const excess = this.cache.size - limit;
    for (let i = 0; i < excess; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  /** Треугольники, вычисленные последним вызовом {@link update}. */
  getTriangles(): Triangle[] {
    return this.triangles;
  }

  /** Уровень детализации, выбранный последним вызовом {@link update}. */
  getLevel(): number {
    return this.level;
  }

  /**
   * Обновить опорный минимальный масштаб (максимальное отдаление), используемый
   * при выборе уровня детализации ({@link selectLodLevel}).
   *
   * Нужен для согласования LOD с изменением предела отдаления контроллера вида
   * при смене авторизации администратора (Требование 11): когда
   * {@link MapViewController.setAdminZoom} пересчитывает `minScale`, это же
   * значение передаётся сюда, чтобы уровень детализации оставался согласованным
   * с новым пределом без пересоздания менеджера. Некорректные значения (не число,
   * `≤ 0`) игнорируются.
   *
   * @param minScale новый минимальный (наиболее отдалённый) масштаб (`> 0`)
   */
  setMinScale(minScale: number): void {
    if (Number.isFinite(minScale) && minScale > 0) {
      this.options.minScale = minScale;
    }
  }

  /** Текущее число записей в кэше тайлов. */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Соседние копии фундаментальной области (матрицы линейной части; обратная
   * совместимость). Для честного учёта обращения ориентации используйте
   * {@link getNeighborIsometries}.
   */
  getNeighbors(): Mobius[] {
    return this.neighbors.map((n) => n.matrix);
  }

  /** Соседние копии как обобщённые изометрии (матрица + признак обращения). */
  getNeighborIsometries(): Isometry[] {
    return this.neighbors;
  }

  /**
   * Единый перечень видимых копий фундаментальной области последнего кадра
   * (Требования 4.1, 4.2, 4.3, 4.5).
   *
   * Возвращает элементы группы `γ` (изометрии Мёбиуса), давшие при последнем
   * {@link update} хотя бы один треугольник, прошедший {@link triangleInBox}
   * в совмещённых координатах отображения `V∘γ`. Перечень:
   * - включает тождество (текущую область) и вершинных соседей, пересекающих
   *   кадр — по геометрическому пересечению, а не по длине слова (Требования 4.2, 4.3);
   * - исключает копии, полностью вышедшие за видимую область при заданном
   *   пределе отдаления (Требование 4.5);
   * - содержит не более {@link MAX_VISIBLE_COPIES} элементов (Требование 4.5).
   *
   * Этот же список — единый источник копий для отрисовки меша, рисунков
   * ({@link module:drawing-layer}) и меток ({@link module:marker-layer}), чем
   * гарантируется правило «метка рисуется в копии тогда и только тогда, когда
   * в ней рисуется контент» (Требование 5.4). Результат кэшируется до
   * следующего {@link update}.
   *
   * @returns копии `γ`, пересекающие видимую область последнего кадра
   */
  getVisibleCopies(): Mobius[] {
    return this.visibleCopiesMatrices;
  }

  /**
   * Единый перечень видимых копий последнего кадра как обобщённые изометрии
   * (матрица + признак обращения ориентации). Источник копий для отрисовки меша,
   * рисунков и меток, честно учитывающий антиголоморфные (зеркальные) копии
   * неориентируемой поверхности (Требование 8).
   */
  getVisibleCopyIsometries(): Isometry[] {
    return this.visibleCopies;
  }

  /** Полностью очистить кэш тайлов. */
  clearCache(): void {
    this.cache.clear();
  }
}

/**
 * Различить новый {@link SurfacePairing} (элементы `all` — {@link Isometry} с
 * полем `matrix`) и устаревший {@link SidePairingResult} (элементы `all` —
 * матрицы {@link Mobius}). Пустой `all` трактуется как устаревший.
 */
function isSurfacePairing(
  pairing: SidePairingResult | SurfacePairing,
): pairing is SurfacePairing {
  const all = (pairing as SurfacePairing).all;
  return Array.isArray(all) && all.length > 0 && 'matrix' in all[0];
}
